import { DEFAULT_EXPIRE_DAYS } from 'constants/cookies';
import Cookies from 'js-cookie';
import { isClientSide } from './host-config';

//#region Types

type StorageObject = {
  /** The data to store. */
  data: unknown;
  /** The timestamp of the data. */
  timestamp: number;
};

//#endregion

//#region Constants

const DEFAULT_FRESHNESS_DAYS = 1;
const USER_FRESHNESS_DAYS = 30; // Users can be logged in 30 days
const TEST_LOCAL_STORAGE_KEY = 'test-localStorage';
//#endregion

const storage = {
  /**
   * Sets the value of the pair identified by key to value, creating a new key/value pair if none existed for key previously.
   *
   * This function is used to store data in the browser's {@link window.localStorage localStorage} or {@link Cookies cookies}.
   * If {@link window.localStorage localStorage} is available, the data is stored in localStorage.
   * Otherwise, the data is stored in {@link Cookies cookies}.
   *
   * When storing data in {@link Cookies cookies}, the data is stored for {@link DEFAULT_EXPIRE_DAYS 7 days} by default.
   *
   * @param key The key to set the value for.
   * @param data The value to set.
   */
  set(key: string, data: unknown) {
    const object: StorageObject = {
      data: data,
      timestamp: new Date().getTime(),
    };
    const objectString = JSON.stringify(object);
    if (this.localStorage.isAvailable()) {
      this.localStorage.set(key, objectString);
    } else {
      Cookies.set(key, objectString, { expires: DEFAULT_EXPIRE_DAYS });
    }
  },
  /**
   * Returns the value of the pair identified by key, or null if the key does not exist.
   *
   * This function is used to retrieve data from the browser's {@link window.localStorage localStorage} or {@link Cookies cookies}.
   * If {@link window.localStorage localStorage} is available, the data is retrieved from localStorage.
   * Otherwise, the data is retrieved from {@link Cookies cookies}.
   *
   * If the data is stale, the data is deleted and undefined is returned.
   *
   * @param key The key to retrieve the value for.
   * @returns The value of the pair identified by key, or `null` if the key does not exist.
   */
  get(key: string) {
    const objectString = this.localStorage.isAvailable() ? this.localStorage.get(key) : Cookies.get(key);
    if (objectString) {
      try {
        const object = JSON.parse(objectString);
        if (object && 'data' in object && 'timestamp' in object) {
          const storageObject = object as StorageObject;
          if (this.isFreshData(storageObject.timestamp, key)) {
            return storageObject.data;
          }
          // If the data is stale, delete the data
          this.delete(key);
        }
      } catch (error: unknown) {
        console.error('Error parsing object from storage', error);
      }
    }
    return null;
  },
  /**
   * Deletes the value of the pair identified by key, if a value is associated with it.
   *
   * This function is used to delete data from the browser's {@link window.localStorage localStorage} or {@link Cookies cookies}.
   * If {@link window.localStorage localStorage} is available, the data is deleted from localStorage.
   * Otherwise, the data is deleted from {@link Cookies cookies}.
   *
   * @param key The key to delete the value for.
   */
  delete(key: string) {
    if (this.localStorage.isAvailable()) {
      this.localStorage.delete(key);
    } else {
      Cookies.remove(key);
    }
  },
  /**
   * Checks if the data is fresh based on the timestamp and the data type.
   *
   * The time limit for the data types is defined in `timeLimitsInDaysByType`.
   *
   * @param timestamp The timestamp of the data.
   * @param dataType The type of data.
   * @returns `true` if the data is fresh, `false` if the data is stale.
   */
  isFreshData(timestamp: number, dataType: string) {
    if (timestamp > this.globalCacheResetTimestamp) {
      const timeLimitInDays = dataType === 'user' ? this.timeLimitsInDaysByType[dataType] : DEFAULT_FRESHNESS_DAYS;
      const currentTime = new Date().getTime();
      const timeDifferenceInSeconds = (currentTime - timestamp) / 1000;
      const timeLimitInSeconds = timeLimitInDays * 24 * 60 * 60;
      return timeDifferenceInSeconds <= timeLimitInSeconds;
    }
  },
  globalCacheResetTimestamp: new Date(2016, 9, 17, 0, 0, 0).getTime(),
  timeLimitsInDaysByType: {
    user: USER_FRESHNESS_DAYS,
  },
  localStorage: {
    /**
     * Checks if the localStorage is available by trying to set and remove an item.
     *
     * @returns `true` if the localStorage is available, `false` otherwise.
     */
    isAvailable() {
      try {
        if (isClientSide() && window?.localStorage) {
          window.localStorage.setItem(TEST_LOCAL_STORAGE_KEY, 'test');
          window.localStorage.removeItem(TEST_LOCAL_STORAGE_KEY);
          return true;
        }
      } catch (error: unknown) {
        console.error('Error setting item in localStorage', error);
      }
      return false;
    },
    /**
     * Sets the value of the pair identified by key to value, creating a new key/value pair if none existed for key previously.
     *
     * @param key The key to set the value for.
     * @param value The value to set.
     */
    set(key: string, value: string) {
      window?.localStorage?.setItem(key, value);
    },
    /**
     * Returns the current value associated with the given key, or null if the given key does not exist.
     *
     * @param key The key to retrieve the value for.
     * @returns The value of the pair identified by key, or undefined if the key does not exist.
     */
    get(key: string) {
      return window?.localStorage?.getItem(key);
    },
    /**
     * Removes the key/value pair with the given key, if a key/value pair with the given key exists.
     *
     * @param key The key to delete the value for.
     */
    delete(key: string) {
      window?.localStorage?.removeItem(key);
    },
  },
};

export default storage;
